import { Card } from '@chakra-ui/react';
import { IAccountRespSchema, IContactRespSchema } from '@monetize/types/app';
import { useMemo, useState } from 'react';
import {
  MBox,
  MButton,
  MCard,
  MCloseButton,
  MDivider,
  MFlex,
  MFormField,
  MHeading,
  MHStack,
  MText,
  MTooltip,
  MVStack,
} from '../../../../../components/Monetize';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useACL } from '../../../../../services/acl/acl';
import { sortAlphabetically } from '../../../../../utils';
import { renderItemContentForAccount } from '../../../../Settings/rules-v3/rules.utils';
import { QuoteContactForm } from './QuoteContactForm';
import QuoteContactsSelectInput from './QuoteContactsSelectInput';

interface IQuoteContactAdditionalRecipientsProps {
  isDisabled?: boolean;
  quoteAccountId: string;
  selectedContacts: IContactRespSchema[];
  selectedExternalEsignContacts: IContactRespSchema[];
  onSelectedContactsChange?: (contact: IContactRespSchema) => void;
  handleCreatedNewContact?: (contact: IContactRespSchema) => void;
  onRemoveAdditionalContact: (contactId: string) => void;
  label: string;
  lengthLimit?: number;
  showAccountSelect?: boolean;
  accountSelectItems?: IAccountRespSchema[];
  isAccountSelectDisabled?: boolean;
}

const AdditionalContactItems = ({
  contact,
  onRemove,
  account,
  showAccount,
  isDisabled,
}: {
  contact: IContactRespSchema;
  onRemove: (id: string) => void;
  account?: IAccountRespSchema;
  showAccount?: boolean;
  isDisabled?: boolean;
}) => {
  const { id, fullName, email } = contact;

  return (
    <MFlex
      key={id}
      flexDirection="row"
      mb={4}
      data-testid="additional-contact-items"
    >
      <MVStack width="full">
        <MText fontSize="md" truncate w="full">
          {fullName}
        </MText>
        {showAccount && account && (
          <MText color="tGray.darkPurple" truncate w="full">
            {account.accountName}
          </MText>
        )}
        <MText color="tGray.darkPurple" truncate w="full">
          {email}
        </MText>
      </MVStack>

      <MTooltip label="Remove Contact" placement="bottom-end">
        {!isDisabled && (
          <MCloseButton
            aria-label="Remove Contact"
            onClick={() => onRemove(id)}
          />
        )}
      </MTooltip>
    </MFlex>
  );
};

export const QuoteContactAdditionalRecipients = ({
  isDisabled,
  quoteAccountId,
  selectedContacts,
  selectedExternalEsignContacts,
  onSelectedContactsChange,
  handleCreatedNewContact = (contact: IContactRespSchema) => {},
  onRemoveAdditionalContact,
  label,
  lengthLimit,
  showAccountSelect,
  accountSelectItems,
  isAccountSelectDisabled,
}: IQuoteContactAdditionalRecipientsProps) => {
  const [addContactActive, setAddContactActive] = useState(false);
  const [newContactOpen, setNewContactOpen] = useState(false);

  const [accountId, setAccountId] = useState<string>(quoteAccountId);
  const { canDo } = useACL();
  const canCreateContact = canDo([['account_contacts', 'create']]);

  const excludeContactIds = useMemo(() => {
    const selectedIds = selectedContacts.map(({ id }) => id);
    if (accountId === quoteAccountId) {
      return [
        ...selectedIds,
        ...selectedExternalEsignContacts.map(({ id }) => id),
      ];
    }
    return selectedIds;
  }, [
    accountId,
    quoteAccountId,
    selectedContacts,
    selectedExternalEsignContacts,
  ]);

  const handleOnNewContactSelect = () => {
    setAddContactActive(false);
    setNewContactOpen(true);
  };

  const onCloseContactForm = (contact?: IContactRespSchema) => {
    if (contact) {
      handleCreatedNewContact && handleCreatedNewContact(contact);
    }

    setNewContactOpen(false);
    setAddContactActive(true);
  };

  return (
    <MCard
      variant="borderless"
      data-testid="additional-contact-recipients"
      mb="2"
    >
      <Card.Body>
        <MHeading size="sm" fontWeight="semibold" pb={4}>
          {label}
        </MHeading>
        <MBox width="full">
          {selectedContacts
            ?.sort(sortAlphabetically('fullName'))
            .map((contact) => (
              <AdditionalContactItems
                key={contact.id}
                contact={contact}
                onRemove={onRemoveAdditionalContact}
                account={accountSelectItems?.find(
                  (account) => account.id === contact.accountId,
                )}
                showAccount={showAccountSelect}
                isDisabled={isDisabled}
              />
            ))}
        </MBox>

        {newContactOpen && (
          <>
            <MDivider />
            <MCard variant="borderless">
              <Card.Body p={0} pt={4}>
                <QuoteContactForm
                  data-testid="additional-contact-form"
                  accountId={accountId}
                  isCcContact
                  isCompact={false}
                  onClose={onCloseContactForm}
                />
              </Card.Body>
            </MCard>
          </>
        )}

        {!addContactActive &&
          !newContactOpen &&
          (!lengthLimit ||
            (lengthLimit && selectedContacts.length < lengthLimit)) && (
            <MBox>
              <MButton
                data-testid="add-additional-contact-btn"
                variant="tertiary"
                size="sm"
                onClick={() => setAddContactActive(true)}
                isDisabled={isDisabled}
              >
                + Add Contact
              </MButton>
            </MBox>
          )}

        {addContactActive &&
          (!lengthLimit ||
            (lengthLimit && selectedContacts.length < lengthLimit)) && (
            <MHStack>
              {showAccountSelect && (
                <MFormField label="Account" flex="1">
                  <MCustomSelect
                    itemTitle="accountName"
                    itemValue="id"
                    items={accountSelectItems}
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInput}
                    renderItemContent={renderItemContentForAccount}
                    value={accountId}
                    onChange={setAccountId}
                    isSelectOpen={false}
                    isDisabled={isAccountSelectDisabled}
                  />
                </MFormField>
              )}
              <MFormField label={showAccountSelect ? 'Contact' : ''} flex="1">
                <QuoteContactsSelectInput
                  accountId={accountId}
                  excludeContactIds={excludeContactIds}
                  onChange={(contact) => {
                    onSelectedContactsChange &&
                      onSelectedContactsChange(contact);
                    if (lengthLimit && selectedContacts.length >= lengthLimit) {
                      setAddContactActive(false);
                      setNewContactOpen(false);
                    }
                  }}
                  onNewContact={handleOnNewContactSelect}
                  canCreateContact={canCreateContact}
                  isDisabled={isDisabled}
                />
              </MFormField>
            </MHStack>
          )}
      </Card.Body>
    </MCard>
  );
};
